import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { kycApi } from '@/api/kyc';
import { useMutation } from '@tanstack/react-query';
import { ProductView } from '@/modules/Product/View';
import { kycHelpers } from '@/utils/helpers/kyc';
import { processProductAnswer } from '@/utils/helpers/kyc/productAnswerProcessing';
import { STATUS } from '@/utils/static/kyc';
import { useSelector } from 'react-redux';
import { usePopups, useLoadingInit, useLoadingState } from '@/hooks';
import {
  useEntityNavigation,
  useRejectedQuestions,
  useEntitySections,
  useReferralToken
} from '@/modules/shared/hooks';

export const Product = ({
  adminView,
  adminEditView = false,
  referralView = false,
  investorId,
  productId
}) => {
  const { referralToken } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { id: paramId, referralId } = useParams();
  const id = adminView ? productId : paramId;
  const { showErrorToast, showLoader, hideLoader } = usePopups();
  const investor = useSelector((state) => state.investor);
  const [error, setError] = useState(undefined);

  const { mutateAsync: setPreProcessingStatus } = useMutation({
    mutationFn: adminView
      ? kycApi.setPreProcessingStatusForAdmin
      : referralView
        ? kycApi.setReferralPreProcessingStatus
        : kycApi.setPreProcessingStatus
  });

  const { rejectedQuestions, rejectedSectionsIndexes, processRejectedQuestions } =
    useRejectedQuestions({
      entityType: 'product',
      adminView,
      investorId
    });

  const { entity: product, isFetching } = useEntitySections({
    entityType: 'product',
    id,
    adminView,
    referralView,
    investorId,
    referralToken,
    onSuccess: async ({ data: productData }) => {
      if (
        productData?.status === STATUS.QUESTION_UNACCEPTED ||
        productData?.status === STATUS.REJECTED
      ) {
        await processRejectedQuestions(productData, productData.status, showErrorToast, hideLoader);
      }
    }
  });

  const {
    currentSection,
    setCurrentSection,
    initialStep,
    setInitialStep,
    onBack,
    finish,
    nextStep: navigationNextStep
  } = useEntityNavigation({
    sections: product?.productSections,
    rejectedSectionsIndexes,
    onFinish: async () => {
      try {
        const res = await setPreProcessingStatus({
          id: id,
          ...(adminView && { investorId: investorId }),
          ...(referralView && referralToken && { token: referralToken })
        });

        if (res) {
          const res2 = await checkProductStatus({
            id: id,
            ...(adminView && { investorId: investor.id }),
            ...(referralView && referralToken && { token: referralToken })
          });

          if (res2)
            adminView
              ? navigate('/investor/' + investorId)
              : referralView
                ? navigate('/referral/' + referralId + '/products')
                : navigate('/profile/products');
        } else {
          adminView
            ? navigate(`/investor/${investorId}/products`)
            : referralView
              ? navigate(`/referral/${referralId}/products`)
              : navigate('/profile/products');
        }
      } catch (e) {
        showErrorToast(e, true);
      }
    }
  });

  useLoadingInit();
  useLoadingState(isFetching);

  useReferralToken({
    paramId,
    referralToken,
    referralView,
    referralId,
    onError: (errorMessage) => setError(errorMessage),
    onSuccess: () => {
      if (error) setError(undefined);
    }
  });

  const { mutateAsync: checkProductStatus } = useMutation({
    mutationFn: adminView
      ? kycApi.checkProductStatusForAdmin
      : referralView
        ? kycApi.checkReferralProductStatus
        : kycApi.checkProductStatus
  });

  const onNext = async (data, isFinish) => {
    try {
      showLoader('Processing');

      const answersIds = [];
      if (product && product.productSections && Array.from(product.productSections).length > 0) {
        const { array, oneByOneRequests } = kycHelpers.collectProductsCurrentAnswers({
          product,
          data,
          currentSection
        });

        const alreadyClearedQuestions = {};
        const apiCall = adminView ? kycApi.saveQuestionProductForAdmin : kycApi.saveQuestionProduct;

        if (oneByOneRequests) {
          const post = async (index) => {
            const thisAnswer = array[index];

            if (
              thisAnswer &&
              (thisAnswer.answer || thisAnswer.availableAnswerId || thisAnswer.file)
            ) {
              await processProductAnswer(thisAnswer, {
                adminView,
                investorId,
                alreadyClearedQuestions,
                rejectedQuestions,
                product,
                answersIds,
                apiCall
              });

              await post(index + 1);
            }
          };

          await post(0);
        } else {
          await Promise.all(
            array
              .filter((answer) => answer.answer || answer.availableAnswerId || answer.file)
              .map((answer) =>
                processProductAnswer(answer, {
                  adminView,
                  investorId,
                  rejectedQuestions,
                  product,
                  answersIds,
                  apiCall
                })
              )
          );
        }
      }

      isFinish ? await finish(answersIds) : await navigationNextStep(answersIds);
    } catch (e) {
      showErrorToast(e, true);
      console.error(e);
    } finally {
      hideLoader();
    }
  };

  const handleNext = async (data) => {
    await onNext(data, false);
  };
  const handleFinish = async (data) => {
    await onNext(data, true);
  };

  const loading = isFetching || !product || product.investorProductId !== id;

  return (
    <div>
      {!loading && (
        <ProductView
          adminEditView={adminEditView}
          referralView={referralView}
          product={product}
          currentSection={currentSection}
          currentStep={initialStep}
          onNext={handleNext}
          onFinish={handleFinish}
          onBack={onBack}
          rejectedQuestions={rejectedQuestions}
          setCurrentSection={setCurrentSection}
          setCurrentStep={setInitialStep}
          disabled={
            investor.accountStatus !== 'ACTIVE' ||
            (adminView || adminEditView
              ? false
              : investor.isAccessibleWithoutVerification
                ? false
                : !investor.isUserVerified)
          }
        />
      )}
    </div>
  );
};

export * from './View';
